/**
 * useFlyAnimation — fly animation state, refs, and lifecycle effects for PrimateVisionV2.
 * Owns the webcam-to-result-pill fly animation (videoSlotRefCallback) and all related
 * BEFORE/AFTER state machine state.
 * Extracted from PrimateVisionV2.tsx (PRI-291 Phase 5)
 */
import { useState, useRef, useCallback, useEffect } from 'react'
import type { ResultPill } from '../../core/components/v2/pv2-shared'

export function useFlyAnimation({ resultPills }: { resultPills: ResultPill[] }) {
  const [afterState, setAfterState] = useState(false);
  const [beforeStateActive, setBeforeStateActive] = useState(true);
  const [carouselVisible, setCarouselVisible] = useState(false);
  const [carouselDismissed, setCarouselDismissed] = useState(false);
  const [slotRevealed, setSlotRevealed] = useState(false);
  const camRef = useRef<HTMLDivElement>(null);
  const threadBottomRef = useRef<HTMLDivElement>(null);
  const desktopThreadAreaRef = useRef<HTMLDivElement>(null);
  const flyThumbnailRef = useRef<string | null>(null);

  // ── Fly animation — rAF-based approach (replaces CSS transitions) ────────
  // Previous CSS-transition approach measured the slot position once and flew
  // to that fixed point, but the slot MOVED during the fly (justifyContent:
  // center redistributes space as the source card shrinks). The clone arrived
  // at the stale position → visible snap.
  //
  // New approach: requestAnimationFrame loop that live-tracks the slot's
  // viewport position every frame. The clone smoothly interpolates from cam
  // toward wherever the slot currently is, so it always lands exactly on
  // target regardless of layout shifts.
  const flyDivRef    = useRef<HTMLDivElement>(null);   // ref to <FlyingWebcamClone>
  const camRectRef   = useRef<DOMRect | null>(null);   // cam rect measured BEFORE state changes
  const flyTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const flyRafRef    = useRef<number>(0);              // active rAF id for cancellation
  const capturedCamElRef = useRef<HTMLElement | null>(null);
  const capturedSlotElRef = useRef<HTMLElement | null>(null); // stored for crossfade at landing




  // Slot callback ref — fires the instant the pending pill's video slot mounts.
  // Kicks off a rAF-based fly that live-tracks the slot every frame.
  const videoSlotRefCallback = useCallback((el: HTMLDivElement | null) => {
    if (!el || !camRectRef.current) return;
    capturedSlotElRef.current = el;

    const camRect  = camRectRef.current;
    camRectRef.current = null; // consume — only trigger once

    const flyDiv   = flyDivRef.current;
    const camEl    = capturedCamElRef.current;
    if (!flyDiv || !camEl) return;

    const D = 520; // fly duration ms

    // ── Easing: approximate cubic-bezier(0.4, 0, 0.2, 1) ────────────
    function ease(t: number) {
      // easeOutQuart — fast departure, smooth deceleration
      return 1 - Math.pow(1 - t, 4);
    }
    function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }

    // ── Step 1: Snap clone to cam's viewport position ────────────────
    flyDiv.style.transition   = 'none';
    flyDiv.style.left         = camRect.left   + 'px';
    flyDiv.style.top          = camRect.top    + 'px';
    flyDiv.style.width        = camRect.width  + 'px';
    flyDiv.style.height       = camRect.height + 'px';
    flyDiv.style.borderRadius = '14px';
    flyDiv.style.opacity      = '1';
    flyDiv.style.display      = 'block';
    camEl.style.visibility    = 'hidden';

    // ── Step 2: rAF fly — live-track slot position every frame ───────
    const startRect = {
      left: camRect.left, top: camRect.top,
      width: camRect.width, height: camRect.height,
    };
    const startTime = performance.now();
    // Capture non-null references for the inner rAF closure (TS null-safety)
    const slotEl_ = el;
    const flyDiv_ = flyDiv;
    const camEl_  = camEl;

    function animate(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / D, 1);
      const e = ease(progress);

      // Live-measure the slot's CURRENT viewport position.
      // This is the key difference from the old CSS-transition approach:
      // the target updates every frame, so layout shifts during the fly
      // (source card shrinking, justify-content redistribution) are
      // automatically tracked. No snap at landing.
      const sr = slotEl_.getBoundingClientRect();

      flyDiv_.style.left         = lerp(startRect.left, sr.left, e) + 'px';
      flyDiv_.style.top          = lerp(startRect.top, sr.top, e) + 'px';
      flyDiv_.style.width        = lerp(startRect.width, sr.width, e) + 'px';
      flyDiv_.style.height       = lerp(startRect.height, sr.height, e) + 'px';
      flyDiv_.style.borderRadius = lerp(14, 0, e) + 'px';

      if (progress < 1) {
        flyRafRef.current = requestAnimationFrame(animate);
      } else {
        // ── Step 3: Fly arrived. Crossfade clone → slot video. ─────────
        // CRITICAL: set slotRevealed NOW (before clone fade) so React renders
        // the local video at opacity:1 via props. If we delay this, React
        // re-renders (e.g. from narrative text arriving) can reset the direct
        // DOM opacity override back to 0, causing a flicker.
        setSlotRevealed(true);

        const slotEl = capturedSlotElRef.current;
        if (slotEl) {
          slotEl.style.background = '#111';
        }
        // Fade out clone over 200ms
        flyDiv_.style.transition = 'opacity 0.2s ease';
        flyDiv_.style.opacity    = '0';

        // ── Step 4: t+200ms — clone fully faded. Clean up. ────────────
        const cleanup = setTimeout(() => {
          flyDiv_.style.display       = 'none';
          flyDiv_.style.transition    = 'none';
          flyDiv_.style.opacity       = '1';
          camEl_.style.visibility     = '';
          capturedCamElRef.current   = null;
          capturedSlotElRef.current  = null;
          flyThumbnailRef.current    = null;
          setBeforeStateActive(false);
          // carouselVisible is now set in startFlyAnimation (immediate, not delayed)
        }, 220); // 200ms fade + 20ms buffer

        flyTimersRef.current = [cleanup];
      }
    }

    // Kick off on next frame (gives browser one paint with clone at start pos)
    flyRafRef.current = requestAnimationFrame(animate);
    flyTimersRef.current = [];
  }, []); // stable — state setters and refs never change

  // ── Reset fly state when pills are cleared ────────────────────────────────
  const prevPillCountRef = useRef(0);
  useEffect(() => {
    if (resultPills.length === 0 && prevPillCountRef.current > 0) {
      // Clear any in-progress fly animation + timers
      cancelAnimationFrame(flyRafRef.current);
      flyRafRef.current = 0;
      flyTimersRef.current.forEach(clearTimeout);
      flyTimersRef.current = [];
      // Hide fly div and restore cam visibility
      if (flyDivRef.current) {
        flyDivRef.current.style.display    = 'none';
        flyDivRef.current.style.transition = 'none';
        flyDivRef.current.style.opacity    = '1';
      }
      if (capturedCamElRef.current) {
        capturedCamElRef.current.style.visibility = '';
        capturedCamElRef.current = null;
      }
      camRectRef.current = null;
      setAfterState(false);
      setBeforeStateActive(true);
      setCarouselVisible(false);
      setCarouselDismissed(false);
      setSlotRevealed(false);
    }
    prevPillCountRef.current = resultPills.length;
  }, [resultPills.length]);

  useEffect(() => {
    if (resultPills.length > 0) {
      threadBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [resultPills.length]);



  return {
    afterState, setAfterState, beforeStateActive, setBeforeStateActive,
    carouselVisible, setCarouselVisible, carouselDismissed, setCarouselDismissed,
    slotRevealed, setSlotRevealed, camRef, flyDivRef, flyTimersRef, flyRafRef,
    capturedCamElRef, camRectRef, flyThumbnailRef, threadBottomRef, desktopThreadAreaRef,
    videoSlotRefCallback,
  };
}
